/**
 * E-Mail-Vorlagen (Textbausteine): Platzhalter-Katalog, Rendering und
 * Aufbereitung der Fall-/Nutzer-/Standortdaten.
 *
 * Die Nutzer- und Standort-Platzhalter sind identisch mit denen der
 * E-Mail-Signatur (signatureTemplate.ts). Anders als dort bleiben nicht
 * auflösbare Platzhalter beim Einfügen in den Composer sichtbar markiert,
 * damit der Anwender sie vor dem Versand ausfüllt oder entfernt.
 */

import { normalizePhone } from '@/utils/signatureTemplate'

export type PlaceholderGroup = 'nutzer' | 'standort' | 'fall' | 'kasko' | 'allgemein'

export interface MailPlaceholder {
  key: string
  label: string
  group: PlaceholderGroup
}

/** Katalog der unterstützten Platzhalter (Reihenfolge = Anzeige). */
export const MAIL_PLACEHOLDERS: readonly MailPlaceholder[] = [
  { key: 'titel', label: 'Titel', group: 'nutzer' },
  { key: 'vorname', label: 'Vorname', group: 'nutzer' },
  { key: 'nachname', label: 'Nachname', group: 'nutzer' },
  { key: 'email', label: 'E-Mail', group: 'nutzer' },
  { key: 'mobil', label: 'Mobilnummer', group: 'nutzer' },
  { key: 'festnetz', label: 'Festnetz', group: 'nutzer' },
  { key: 'benutzername', label: 'Benutzername', group: 'nutzer' },
  { key: 'organisation', label: 'Unternehmen', group: 'nutzer' },
  { key: 'webseite', label: 'Webseite', group: 'nutzer' },
  { key: 'standort', label: 'Standort', group: 'standort' },
  { key: 'standort_adresse', label: 'Standort-Adresse', group: 'standort' },
  { key: 'standort_telefon', label: 'Standort-Telefon', group: 'standort' },
  { key: 'standort_email', label: 'Standort-E-Mail', group: 'standort' },
  { key: 'aktenzeichen', label: 'Aktenzeichen', group: 'fall' },
  { key: 'kunde_name', label: 'Kunde / Mandant', group: 'fall' },
  { key: 'kunde_anrede', label: 'Anrede Kunde', group: 'fall' },
  { key: 'kennzeichen', label: 'Kennzeichen', group: 'fall' },
  { key: 'unfalldatum', label: 'Unfalldatum', group: 'fall' },
  { key: 'gegner_name', label: 'Gegner', group: 'fall' },
  { key: 'gegner_kennzeichen', label: 'Gegner-Kennzeichen', group: 'fall' },
  { key: 'versicherung_name', label: 'Gegn. Versicherung', group: 'fall' },
  { key: 'schadennummer', label: 'Schadennummer', group: 'fall' },
  { key: 'sachbearbeiter', label: 'Sachbearbeiter', group: 'fall' },
  { key: 'werkstatt', label: 'Werkstatt / Autohaus', group: 'kasko' },
  { key: 'versicherungsnehmer', label: 'Versicherungsnehmer', group: 'kasko' },
  { key: 'kasko_versicherung', label: 'Kaskoversicherung', group: 'kasko' },
  { key: 'versicherungsscheinnummer', label: 'Versicherungsscheinnummer', group: 'kasko' },
  { key: 'kasko_schadennummer', label: 'Schadennummer (Kasko)', group: 'kasko' },
  { key: 'schadentag', label: 'Schadentag', group: 'kasko' },
  { key: 'selbstbeteiligung', label: 'Selbstbeteiligung', group: 'kasko' },
  { key: 'datum', label: 'Heutiges Datum', group: 'allgemein' },
  { key: 'frist_datum', label: 'Frist (heute + 14 Tage)', group: 'allgemein' },
] as const

export const PLACEHOLDER_GROUP_LABELS: Record<PlaceholderGroup, string> = {
  nutzer: 'Absender',
  standort: 'Standort',
  fall: 'Fall',
  kasko: 'Kasko',
  allgemein: 'Allgemein',
}

export type MailTemplateValues = Record<string, string | null | undefined>

/** Beispieldaten für die Live-Vorschau im Vorlagen-Editor. */
export const SAMPLE_TEMPLATE_VALUES: MailTemplateValues = {
  titel: '',
  vorname: 'Max',
  nachname: 'Mustermann',
  email: 'max.mustermann@kanzlei-beispiel.de',
  mobil: '+49 170 1234567',
  festnetz: '+49 30 1234560',
  benutzername: 'Max Mustermann',
  organisation: 'Kanzlei Beispiel & Partner',
  webseite: 'https://www.kanzlei-beispiel.de',
  standort: 'Berlin',
  standort_adresse: 'Musterstraße 1, 10115 Berlin',
  standort_telefon: '+49 30 1234560',
  standort_email: 'berlin@kanzlei-beispiel.de',
  aktenzeichen: '2026-0142',
  kunde_name: 'Erika Musterfrau',
  kunde_anrede: 'Sehr geehrte Frau Musterfrau,',
  kennzeichen: 'B-AB 1234',
  unfalldatum: '03.09.2026',
  gegner_name: 'Hans Beispiel',
  gegner_kennzeichen: 'M-XY 987',
  versicherung_name: 'Beispiel Versicherung AG',
  schadennummer: 'SN-2026-77891',
  sachbearbeiter: 'Max Mustermann',
  werkstatt: 'Autohaus Beispiel GmbH',
  versicherungsnehmer: 'Erika Musterfrau',
  kasko_versicherung: 'Muster Kasko AG',
  versicherungsscheinnummer: 'KV-123456789',
  kasko_schadennummer: 'KS-2026-4711',
  schadentag: '03.09.2026',
  selbstbeteiligung: '300,00 €',
  datum: '08.09.2026',
  frist_datum: '22.09.2026',
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g
const TAG_OR_PLACEHOLDER_RE = /(<[^>]*>)|\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

/** CSS-Klasse des Markers für nicht aufgelöste Platzhalter. */
export const MISSING_PLACEHOLDER_CLASS = 'mail-tpl-missing'

function escapeHtml (value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Alle im Text verwendeten Platzhalter-Schlüssel (dedupliziert, in Reihenfolge des Auftretens). */
export function extractPlaceholders (text: string): string[] {
  const out: string[] = []
  for (const m of (text || '').matchAll(PLACEHOLDER_RE)) {
    const key = m[1] as string
    if (!out.includes(key)) {
      out.push(key)
    }
  }
  return out
}

export function isKnownPlaceholder (key: string): boolean {
  return MAIL_PLACEHOLDERS.some(p => p.key === key)
}

export interface RenderOptions {
  /** Dekoriert aufgelöste Werte (z. B. Hervorhebung in der Vorschau). */
  wrapValue?: (value: string, key: string) => string
  /**
   * Darstellung nicht auflösbarer Platzhalter im HTML:
   *  - "mark" (Vorgabe): sichtbar markiert, damit der Anwender sie ausfüllt
   *  - "keep": unverändert als {{key}} stehen lassen
   *  - "remove": entfernen
   */
  unresolved?: 'mark' | 'keep' | 'remove'
}

function resolveValue (values: MailTemplateValues, key: string): string {
  const v = values[key]
  return (v ?? '').toString().trim()
}

function unresolvedHtml (key: string, mode: RenderOptions['unresolved']): string {
  switch (mode) {
    case 'keep': {
      return `{{${key}}}`
    }
    case 'remove': {
      return ''
    }
    default: {
      return `<span class="${MISSING_PLACEHOLDER_CLASS}" data-placeholder="${key}" title="Platzhalter konnte nicht automatisch befüllt werden – bitte ergänzen">{{${key}}}</span>`
    }
  }
}

/**
 * Ersetzt Platzhalter im HTML-Text einer Vorlage. Platzhalter in Attributen
 * (href="mailto:{{email}}") werden nackt ersetzt; leere Werte im Text werden
 * je nach `unresolved` markiert, belassen oder entfernt.
 */
export function renderMailTemplateHtml (
  html: string,
  values: MailTemplateValues,
  options: RenderOptions = {},
): string {
  if (!html) {
    return ''
  }
  const wrap = options.wrapValue ?? ((v: string) => v)
  const mode = options.unresolved ?? 'mark'

  const withAttrs = html.replace(/<[^>]*>/g, tag =>
    tag.replace(PLACEHOLDER_RE, (_m, key: string) => escapeHtml(resolveValue(values, key))),
  )

  return withAttrs.replace(TAG_OR_PLACEHOLDER_RE, (_m, tag: string | undefined, key: string | undefined) => {
    if (tag) {
      return tag
    }
    const k = key as string
    const value = resolveValue(values, k)
    return value ? wrap(escapeHtml(value), k) : unresolvedHtml(k, mode)
  })
}

/**
 * Ersetzt Platzhalter in einer Betreffzeile (reiner Text). Nicht auflösbare
 * Platzhalter bleiben als {{key}} sichtbar stehen.
 */
export function renderMailTemplateSubject (subject: string, values: MailTemplateValues): string {
  if (!subject) {
    return ''
  }
  return subject
    .replace(PLACEHOLDER_RE, (_m, key: string) => resolveValue(values, key) || `{{${key}}}`)
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/** Schlüssel, die mit den übergebenen Werten NICHT befüllt werden können. */
export function unresolvedPlaceholders (text: string, values: MailTemplateValues): string[] {
  return extractPlaceholders(text).filter(key => !resolveValue(values, key))
}

// ── Werte aus Nutzer-, Standort- und Falldaten ─────────────────────────────

export interface TemplateUserInfo {
  title?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  mobile?: string | null
  landline?: string | null
  userName?: string | null
  organizationName?: string | null
  website?: string | null
}

export interface TemplateLocationInfo {
  name?: string | null
  address?: string | null
  streetNo?: string | null
  zip?: string | null
  city?: string | null
  landline?: string | null
  email?: string | null
}

export interface TemplateCaseInfo {
  caseNumber?: string | null
  clients?: Array<Record<string, unknown>> | null
  vehicles?: Array<Record<string, unknown>> | null
  opponents?: Array<Record<string, unknown>> | null
  accidents?: Array<Record<string, unknown>> | null
  /** Beteiligte der Akte – liefern die Werkstatt für Kasko-Schreiben. */
  involved?: readonly InvolvedParty[] | null
}

/** Beteiligter der Akte, soweit die Platzhalter ihn brauchen. */
export interface InvolvedParty {
  name?: string | null
  companyName?: string | null
  roles?: unknown
  removedAt?: string | null
}

export interface BuildValuesInput {
  user?: TemplateUserInfo | null
  location?: TemplateLocationInfo | null
  case?: TemplateCaseInfo | null
  /** Bezugsdatum für {{datum}}/{{frist_datum}} (Vorgabe: jetzt). */
  today?: Date
  /** Fristlänge in Tagen für {{frist_datum}} (Vorgabe 14). */
  deadlineDays?: number
}

const str = (v: unknown): string => (v == null ? '' : String(v)).trim()

/** Datum YYYY-MM-DD (oder ISO) → DD.MM.YYYY; andere Formate unverändert. */
export function formatDateDE (value: unknown): string {
  const s = str(value)
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s
}

function formatDate (d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${d.getFullYear()}`
}

function personName (title: unknown, first: unknown, last: unknown): string {
  return [str(title), str(first), str(last)].filter(Boolean).join(' ')
}

/** Name eines Mandanten: Firma, sonst "Vorname Nachname", sonst Kurzname. */
export function clientDisplayName (client: Record<string, unknown> | undefined): string {
  if (!client) {
    return ''
  }
  return str(client.company)
    || personName(client.title, client.firstName, client.lastName)
    || str(client.client)
}

/** Briefanrede eines Mandanten ("Sehr geehrte Frau Musterfrau,"). */
export function clientSalutation (client: Record<string, unknown> | undefined): string {
  if (!client) {
    return ''
  }
  const last = str(client.lastName)
  const title = str(client.title)
  const salutation = str(client.salutation).toLowerCase()
  if (last && salutation.startsWith('frau')) {
    return `Sehr geehrte Frau ${[title, last].filter(Boolean).join(' ')},`
  }
  if (last && salutation.startsWith('herr')) {
    return `Sehr geehrter Herr ${[title, last].filter(Boolean).join(' ')},`
  }
  return 'Sehr geehrte Damen und Herren,'
}

/** Name des Unfallgegners (Halter; Firma vor Person). */
export function opponentDisplayName (opponent: Record<string, unknown> | undefined): string {
  if (!opponent) {
    return ''
  }
  return str(opponent.holderCompany)
    || personName(opponent.holderTitle, opponent.holderFirstName, opponent.holderLastName)
    || personName(opponent.driverTitle, opponent.driverFirstName, opponent.driverLastName)
}

const EURO = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })

/** Betrag als „300,00 €“; leer, wenn keine Zahl. */
export function formatEuroAmount (value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  return Number.isFinite(n) ? EURO.format(n) : ''
}

/** Selbstbeteiligung passend zur Kasko-Art des Fahrzeugs (Voll-/Teilkasko). */
export function kaskoDeductible (vehicle: Record<string, unknown> | undefined): string {
  if (!vehicle) {
    return ''
  }
  const type = str(vehicle.insuranceType)
  const specific = type === 'Vollkasko'
    ? vehicle.insuranceDeductibleFull
    : (type === 'Teilkasko' ? vehicle.insuranceDeductiblePartial : null)
  return formatEuroAmount(specific) || formatEuroAmount(vehicle.insuranceDeductible)
}

/** Rolle der Werkstatt unter den Beteiligten (wie im Kasko-Cockpit). */
const WORKSHOP_ROLE = 'Autohaus'

/**
 * Ausführende Werkstatt: das Autohaus unter den Beteiligten, sonst die
 * Werkstatt aus den Reparaturangaben des Fahrzeugs.
 */
export function workshopName (involved: readonly InvolvedParty[] | null | undefined, vehicle: Record<string, unknown> | undefined): string {
  const workshop = (involved || []).find(p =>
    !p.removedAt && Array.isArray(p.roles) && p.roles.includes(WORKSHOP_ROLE))
  return str(workshop?.companyName) || str(workshop?.name) || str(vehicle?.repairWorkshop)
}

/**
 * Baut die Platzhalter-Werte aus den im Composer ohnehin vorhandenen Daten
 * (angemeldeter Nutzer, Signatur-Standort, Fall-Ressourcen der
 * Betreff-Bausteine). Fehlende Angaben ergeben leere Werte.
 */
export function buildTemplateValues (input: BuildValuesInput): MailTemplateValues {
  const user = input.user ?? {}
  const loc = input.location ?? {}
  const c = input.case ?? {}
  const today = input.today ?? new Date()
  const deadline = new Date(today)
  deadline.setDate(deadline.getDate() + (input.deadlineDays ?? 14))

  const locAddress = str(loc.address)
    || [str(loc.streetNo), [str(loc.zip), str(loc.city)].filter(Boolean).join(' ')].filter(Boolean).join(', ')

  const firstClient = c.clients?.[0]
  const firstVehicle = c.vehicles?.[0]
  const firstOpponent = c.opponents?.[0]
  const firstAccident = c.accidents?.[0]

  const sachbearbeiter = personName(user.title, user.firstName, user.lastName) || str(user.userName)

  return {
    titel: str(user.title),
    vorname: str(user.firstName),
    nachname: str(user.lastName),
    email: str(user.email),
    mobil: normalizePhone(user.mobile),
    festnetz: normalizePhone(user.landline),
    benutzername: str(user.userName),
    organisation: str(user.organizationName),
    webseite: str(user.website),
    standort: str(loc.name),
    standort_adresse: locAddress,
    standort_telefon: normalizePhone(loc.landline),
    standort_email: str(loc.email),
    aktenzeichen: str(c.caseNumber),
    kunde_name: clientDisplayName(firstClient),
    kunde_anrede: firstClient ? clientSalutation(firstClient) : '',
    kennzeichen: str(firstVehicle?.licensePlate),
    unfalldatum: formatDateDE(firstAccident?.accidentDate),
    gegner_name: opponentDisplayName(firstOpponent),
    gegner_kennzeichen: str(firstOpponent?.licensePlate),
    versicherung_name: str(firstOpponent?.insuranceName),
    schadennummer: str(firstOpponent?.insuranceCaseNumber),
    sachbearbeiter,
    werkstatt: workshopName(c.involved, firstVehicle),
    versicherungsnehmer: clientDisplayName(firstClient),
    kasko_versicherung: str(firstVehicle?.insuranceName),
    versicherungsscheinnummer: str(firstVehicle?.insurancePolicyNumber),
    kasko_schadennummer: str(firstVehicle?.insuranceCaseNumber),
    schadentag: formatDateDE(firstAccident?.accidentDate),
    selbstbeteiligung: kaskoDeductible(firstVehicle),
    datum: formatDate(today),
    frist_datum: formatDate(deadline),
  }
}

// ── Import "Aus Text/HTML einfügen" ────────────────────────────────────────

/** Reinen Text (z. B. aus Word/Outlook kopiert) in einfaches HTML wandeln. */
export function plainTextToHtml (text: string): string {
  const normalized = (text || '').replace(/\r\n?/g, '\n').trim()
  if (!normalized) {
    return ''
  }
  return normalized
    .split('\n')
    .map(line => escapeHtml(line))
    .join('<br>')
}

/**
 * Bereinigt eingefügtes HTML für den Editor: nimmt nur den <body>-Inhalt,
 * entfernt Skripte, Styles, Kommentare und Event-Handler-Attribute.
 */
export function sanitizeImportedHtml (html = ''): string {
  let out = html
  const body = /<body[^>]*>([\s\S]*?)<\/body>/i.exec(out)
  if (body) {
    out = body[1] ?? ''
  }
  out = out
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|head|title|meta|link)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<(meta|link|base)[^>]*\/?>/gi, '')
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(href|src)\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi, '')
  return out.trim()
}

/** Erkennt, ob ein eingefügter Text bereits HTML ist. */
export function looksLikeHtml (text: string): boolean {
  return /<\s*(html|body|div|p|br|span|table|strong|b|i|em|ul|ol|a)\b[^>]*>/i.test(text || '')
}

/** Import-Eingabe (Text oder HTML) in Editor-HTML umwandeln. */
export function importedContentToHtml (raw: string): string {
  return looksLikeHtml(raw) ? sanitizeImportedHtml(raw) : plainTextToHtml(raw)
}

/** HTML grob in Text wandeln (für Listenvorschauen). */
export function htmlToPreviewText (html: string, maxLen = 140): string {
  const text = (html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|tr)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > maxLen ? `${text.slice(0, maxLen - 1)}…` : text
}
